import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Customer, Staff, Treatment } from '@/lib/types';
import { Banner, Card, Field, PageHeader, Pill, inputStyle, useToast } from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { CustomerPicker } from '@/components/CustomerPicker';
import { newKey, ntd } from '@/lib/format';

export function QuickAddPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();

  const [customerId, setCustomerId] = useState('');
  const [creating, setCreating] = useState({ name: '', phone: '' });
  const [amount, setAmount] = useState(0);
  const [booking, setBooking] = useState({ treatmentId: '', doctorId: '' });

  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });
  const doctors = (staff.data ?? []).filter((s) => s.role === 'DOCTOR');

  const wallet = useQuery({
    queryKey: ['wallet', customerId],
    enabled: !!customerId,
    queryFn: () => api.get<{ balanceCents: number }>(`/billing/wallet/${customerId}`),
  });

  const quickCreate = useMutation({
    mutationFn: () => api.post<Customer>('/customers', { name: creating.name, phone: creating.phone }),
    onSuccess: (c) => { ok('舊客已補登'); setCustomerId(c.id); setCreating({ name: '', phone: '' }); },
    onError: fail,
  });

  const deposit = useMutation({
    mutationFn: () => api.post('/billing/wallet/deposit', { customerId, amountCents: Math.round(amount * 100), idempotencyKey: newKey() }),
    onSuccess: () => { ok('儲值完成'); setAmount(0); void qc.invalidateQueries({ queryKey: ['wallet', customerId] }); },
    onError: fail,
  });

  const quickBook = useMutation({
    mutationFn: () => api.post('/appointments', { customerId, treatmentId: booking.treatmentId, doctorId: booking.doctorId, startAt: new Date().toISOString() }),
    onSuccess: () => { ok('已立即開立預約（三維檢測通過）'); setBooking({ treatmentId: '', doctorId: '' }); },
    onError: fail,
  });

  return (
    <div>
      <PageHeader title="30 秒舊客補登" subtitle="現場快速：找舊客 → 立即儲值 / 立即開單，免逐頁切換" />
      <Banner msg={msg} />

      <Card title="① 找舊客 / 補登新客" icon={<Icon d={I.search} size={14} style={{ color: 'var(--primary)' }} />}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="以姓名 / 電話搜尋現有顧客">
            <CustomerPicker value={customerId} onChange={setCustomerId} />
          </Field>
          <div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6 }}>找不到？30 秒補登新客</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={inputStyle} placeholder="姓名" value={creating.name} onChange={(e) => setCreating((s) => ({ ...s, name: e.target.value }))} />
              <input style={inputStyle} placeholder="電話" value={creating.phone} onChange={(e) => setCreating((s) => ({ ...s, phone: e.target.value }))} />
              <button className="btn btn-primary" disabled={!creating.name || !creating.phone || quickCreate.isPending} onClick={() => quickCreate.mutate()}>補登</button>
            </div>
          </div>
        </div>
        {customerId && (
          <div style={{ marginTop: 12 }}>
            <Pill bg="var(--success-bg)" color="var(--success)">已選定顧客</Pill>
            {wallet.data && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--ink-2)' }}>目前餘額 <b className="num">{ntd(wallet.data.balanceCents)}</b></span>}
          </div>
        )}
      </Card>

      {customerId && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Card title="② 快速儲值" icon={<Icon d={I.wallet} size={14} style={{ color: 'var(--primary)' }} />}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Field label="儲值金額（元）"><input type="number" className="num" style={inputStyle} value={amount} onChange={(e) => setAmount(Number(e.target.value))} /></Field>
              <button className="btn btn-primary" disabled={amount < 1 || deposit.isPending} onClick={() => deposit.mutate()}>儲值</button>
            </div>
          </Card>

          <Card title="③ 立即開單預約" icon={<Icon d={I.cal} size={14} style={{ color: 'var(--primary)' }} />}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <Field label="療程"><Select value={booking.treatmentId} onChange={(v) => setBooking((s) => ({ ...s, treatmentId: v }))} placeholder="選擇療程" options={(treatments.data ?? []).map((t) => ({ value: t.id, label: t.name }))} /></Field>
              <Field label="醫師"><Select value={booking.doctorId} onChange={(v) => setBooking((s) => ({ ...s, doctorId: v }))} placeholder="選擇醫師" options={doctors.map((d) => ({ value: d.id, label: d.name }))} /></Field>
              <button className="btn btn-primary" disabled={!booking.treatmentId || !booking.doctorId || quickBook.isPending} onClick={() => quickBook.mutate()}>立即預約</button>
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--ink-4)', marginTop: 6 }}>開始時間＝現在；系統仍執行三維資源衝突檢測。</div>
          </Card>
        </div>
      )}
    </div>
  );
}
